import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router-dom';

import { type GetDetailedTotemsOfOrganizationQuery, TotemState } from 'data/__generated__';
import { numberColumn, booleanColumn, multiSelectColumn } from 'components/DataTable';

import { StatusTag } from '../StatusTag';
import { TOTEMS_STATES_LABELS } from './states';

export type OrganizationTotemRow = GetDetailedTotemsOfOrganizationQuery['totemsOfOrganization'][number];

export type OrganizationTotemFilterInput = {
    searchValue: string;
    state: TotemState[];
};

const EmptyCell = () => <span className="text-muted-foreground">—</span>;

const formatAmount = (amount: number | null | undefined) => (amount == null ? '—' : `${amount} €`);

export const organizationTotemsColumns: ColumnDef<OrganizationTotemRow, unknown>[] = [
    {
        id: 'name',
        header: 'Nom',
        meta: { defaultVisible: true },
        accessorFn: (row) => row.name || `TOTEM #${row.number}`,
        cell: ({ row }) => row.original.name || `TOTEM #${row.original.number}`,
    },
    numberColumn<OrganizationTotemRow, OrganizationTotemFilterInput>({
        accessorKey: 'number',
        header: 'Numéro',
        defaultVisible: true,
    }),
    {
        id: 'address',
        header: 'Adresse',
        meta: { defaultVisible: true },
        accessorFn: (row) => [row.address, row.zipcode, row.city].filter(Boolean).join(' '),
        cell: ({ row }) => {
            const { address, zipcode, city } = row.original;
            if (!address && !city) return <EmptyCell />;
            return (
                <div className="flex flex-col">
                    <span>{address || '—'}</span>
                    <span className="text-muted-foreground">{[zipcode, city].filter(Boolean).join(' ')}</span>
                </div>
            );
        },
    },
    {
        id: 'mainUser',
        header: 'Admin',
        meta: { defaultVisible: true },
        accessorFn: (row) => row.mainUser?.fullName ?? '',
        cell: ({ row }) => row.original.mainUser?.fullName || <EmptyCell />,
    },
    {
        id: 'minimumTotalAmount',
        header: 'Min. commande',
        meta: { defaultVisible: true },
        accessorFn: (row) => row.minimumOrderAmountInfo.minimumTotalAmount,
        cell: ({ row }) => formatAmount(row.original.minimumOrderAmountInfo.minimumTotalAmount),
    },
    multiSelectColumn<OrganizationTotemRow, OrganizationTotemFilterInput>({
        accessorKey: 'state',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(TotemState).map((state) => ({
            value: state,
            label: TOTEMS_STATES_LABELS[state] ?? state,
        })),
        renderOption: (option) => <StatusTag state={option.value as TotemState} />,
        cell: ({ row }) => <StatusTag state={row.original.state} />,
    }),
    {
        id: 'microstore',
        header: 'Store',
        meta: { defaultVisible: true },
        accessorFn: (row) => row.microstore?.name ?? '',
        cell: ({ row }) =>
            row.original.microstore ? (
                <Link
                    to={`/site/${row.original.microstore._id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                    onClick={(event) => event.stopPropagation()}
                >
                    {row.original.microstore.name}
                </Link>
            ) : (
                <EmptyCell />
            ),
    },
    numberColumn<OrganizationTotemRow, OrganizationTotemFilterInput>({
        accessorKey: 'accessTime',
        header: "Temps d'accès",
        defaultVisible: false,
    }),
    booleanColumn<OrganizationTotemRow, OrganizationTotemFilterInput>({
        accessorKey: 'needSignature',
        header: 'Signature',
        defaultVisible: false,
    }),
];
